/**
 * WhenBranchCard — renders one parsed `WHEN [NOT] MATCHED [BY …] [AND cond]
 * THEN <action>` arm of a MERGE statement. Each branch ships an action-kind
 * dropdown (`MergeActionKindDropdown` — real once `editReady` and the
 * §2.5 decision-3/4 gates allow it, else the § 1.4 disabled scaffold), a
 * remove button (real once `editReady` and this isn't the sole remaining
 * branch — decision 6), an `AND <cond>` pill (editable when `editContext` +
 * `mergeBranchSelector` are supplied), and the action body —
 * `SetAssignmentsList` (UPDATE), an `InsertColumnMap` + values strip
 * (INSERT), text-only (DELETE / DO NOTHING).
 *
 * VQE-Edit wiring: when `editContext` AND `mergeBranchSelector` are supplied,
 * the AND-condition pill becomes inline-editable and the nested SET list (for
 * UPDATE branches) routes through the MERGE-branch-aware ops via
 * `SetAssignmentsList(mergeBranch=…)`. Without these props the disabled
 * VQE-RO scaffolding renders unchanged.
 *
 * § 1.4 disabled scaffolding is asserted against this component's testids in
 * the MergeRenderer.test.tsx audit.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { toCommitCtx, type VqeEditContext } from '../../edit/editContext';
import { computeRemoveBranchGate, removeMergeBranch } from '../../edit/edits/mergeBranchOps';
import type { MergeBranchSelector } from '../../edit/edits/modifyMergeBranch';
import { AddButton } from '../common/AddButton';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { InsertColumnMap } from '../insert/InsertColumnMap';
import { ClauseCard } from '../select/ClauseCard';
import { SetAssignmentsList } from '../update/SetAssignmentsList';
import { MergeActionKindDropdown } from './MergeActionKindDropdown';
import { MergeBranchConditionPill } from './MergeBranchConditionPill';
import type { MergeAction, WhenBranch } from './parseMergeBranch';
import { vqeCopy } from '../../ux/vqeCopy';

export interface WhenBranchCardProps {
  readonly branch: WhenBranch;
  readonly index: number;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly mergeTargetText: string;
  /**
   * Optional VQE-Edit context. When undefined, disabled VQE-RO scaffolding
   * renders unchanged. When supplied alongside `mergeBranchSelector`, the
   * AND condition becomes editable and the nested UPDATE SET list routes
   * through MERGE-branch-aware ops.
   */
  readonly editContext?: VqeEditContext;
  /**
   * Maps the renderer's flat branch index to the SR-Edit (branchKind,
   * branchIndex) pair the planner uses to pick `mergeWhenMatched[i]` /
   * `mergeWhenNotMatched[i]`. Computed by `MergeRenderer` and passed down so
   * each card knows where its edits land. May be omitted for branches that
   * have no SR-Edit counterpart (e.g. `WHEN NOT MATCHED BY SOURCE` on
   * dialects where the SR-Edit extractor does not yet split that arm out).
   */
  readonly mergeBranchSelector?: MergeBranchSelector;
  /**
   * Doc-order count of every WHEN branch in the statement (all match kinds).
   * Computed by `MergeRenderer` and threaded down so the remove button can
   * refuse to remove the last remaining branch (decision 6) without needing
   * its own view of siblings. Defaults to 1 (conservative: unknown ⇒ treat as
   * sole branch) so existing callers that omit it stay disabled unchanged.
   */
  readonly totalBranchCount?: number;
}

const MATCH_LABELS: Record<WhenBranch['matchKind'], string> = {
  'matched': 'WHEN MATCHED',
  'not-matched': 'WHEN NOT MATCHED',
  'not-matched-by-source': 'WHEN NOT MATCHED BY SOURCE',
};

export function WhenBranchCard({
  branch,
  index,
  bufferText,
  options,
  mergeTargetText,
  editContext,
  mergeBranchSelector,
  totalBranchCount = 1,
}: WhenBranchCardProps) {
  const testId = `vqe-merge-when-${index}`;
  const headerLabel = branch.conditionText
    ? `${MATCH_LABELS[branch.matchKind]} AND …`
    : MATCH_LABELS[branch.matchKind];
  const conditionEditable = !!editContext && !!mergeBranchSelector;
  const removeGate = computeRemoveBranchGate(editContext, mergeBranchSelector, totalBranchCount);

  return (
    <ClauseCard
      title={headerLabel}
      testId={testId}
      accessory={
        <RemoveButton
          testId={`${testId}-remove`}
          ariaLabel="Remove WHEN branch"
          disabled={removeGate.disabled}
          tooltip={removeGate.disabled ? removeGate.tooltip : undefined}
          onClick={
            !removeGate.disabled && editContext && mergeBranchSelector
              ? () => {
                  removeMergeBranch(mergeBranchSelector, toCommitCtx(editContext), {
                    structure: safeExtractStructure(editContext),
                  }).catch(recordEditFailure);
                }
              : undefined
          }
        />
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className="text-muted-foreground"
            data-testid={`${testId}-match-kind`}
          >
            {MATCH_LABELS[branch.matchKind]}
          </span>
          {branch.conditionText !== null ? (
            <>
              <span aria-hidden className="text-muted-foreground/70">AND</span>
              {conditionEditable && editContext && mergeBranchSelector ? (
                <MergeBranchConditionPill
                  testId={`${testId}-condition`}
                  text={branch.conditionText}
                  selector={mergeBranchSelector}
                  editContext={editContext}
                />
              ) : (
                <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
                  <span
                    className="rounded-md border border-border bg-muted/30 px-2 py-0.5 font-mono"
                    data-testid={`${testId}-condition`}
                  >
                    {branch.conditionText}
                  </span>
                </VqeTooltip>
              )}
            </>
          ) : null}
          <span aria-hidden className="text-muted-foreground/70">→</span>
          <MergeActionKindDropdown
            branch={branch}
            selector={mergeBranchSelector}
            editContext={editContext}
            options={options}
            testIdPrefix={testId}
          />
        </div>

        <ActionBody
          action={branch.action}
          bufferText={bufferText}
          options={options}
          testId={testId}
          mergeTargetText={mergeTargetText}
          editContext={editContext}
          mergeBranchSelector={mergeBranchSelector}
        />
      </div>
    </ClauseCard>
  );
}

interface ActionBodyProps {
  readonly action: MergeAction;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly testId: string;
  readonly mergeTargetText: string;
  readonly editContext?: VqeEditContext;
  readonly mergeBranchSelector?: MergeBranchSelector;
}

function ActionBody({
  action,
  bufferText,
  options,
  testId,
  mergeTargetText,
  editContext,
  mergeBranchSelector,
}: ActionBodyProps) {
  if (action.kind === 'update' && action.setStart !== null && action.setEnd !== null) {
    return (
      <div data-testid={`${testId}-action-update`}>
        <SetAssignmentsList
          bufferText={bufferText}
          start={action.setStart}
          end={action.setEnd}
          options={options}
          testIdPrefix={`${testId}-update-set`}
          editContext={mergeBranchSelector ? editContext : undefined}
          mergeBranch={mergeBranchSelector}
          displayTargetsUnqualified
        />
      </div>
    );
  }

  if (action.kind === 'insert') {
    const valuesText =
      action.valuesStart !== null && action.valuesEnd !== null
        ? bufferText.slice(action.valuesStart, action.valuesEnd)
        : null;
    return (
      <div className="flex flex-col gap-2" data-testid={`${testId}-action-insert`}>
        <InsertColumnMap
          target={mergeTargetText}
          columns={action.insertColumns}
          title="Insert columns"
          testIdPrefix={`${testId}-insert`}
        />
        {valuesText !== null ? (
          <VqeTooltip content={vqeCopy.disabled.editingUnavailable} block>
            <div
              className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs"
              data-testid={`${testId}-insert-values`}
            >
              <span className="mr-1.5 text-muted-foreground">VALUES</span>
              {valuesText}
            </div>
          </VqeTooltip>
        ) : null}
        <AddButton label={vqeCopy.actions.addValue} testId={`${testId}-insert-add-value`} />
      </div>
    );
  }

  if (action.kind === 'delete') {
    return (
      <p className="text-xs italic text-muted-foreground" data-testid={`${testId}-action-delete`}>
        Deletes the matched target row.
      </p>
    );
  }

  if (action.kind === 'do-nothing') {
    return (
      <p className="text-xs italic text-muted-foreground" data-testid={`${testId}-action-do-nothing`}>
        DO NOTHING — no row affected.
      </p>
    );
  }

  return (
    <p className="text-xs italic text-muted-foreground" data-testid={`${testId}-action-unknown`}>
      Action body could not be parsed.
    </p>
  );
}
